import { ChangeEventHandler, useCallback, useEffect, useMemo, useRef, useState } from "react";
import Modal from "../../components/Shared/Modal";
import QrCode from "qrcode.react";
import { IOrder, IOrderLineItem } from "shopify-api-node";
import ModalSection from "../../components/Modal/ModalSection";
import { TDriverIndexResult, useDriverRegistration } from "../../context/DriverRegistration";
import { IDriver } from "../../interfaces/BookingRegistration";
import LoadingSpinner from "../../components/Loading";
import { deepCopy, getAgeInYears, getDatetimeFromSku, getTrackFromSku, getTrackDisplayName, getLiveUsersRegistrationContent, getAgeGroupDisplayName, getDateFromSku } from "../../utilities";
import { TBookingType, BOOKING_TYPES } from "../../interfaces";
import { IFirebaseBookingDocument, IRegistration } from "../../interfaces/driverRegistration";
import { DateTime } from "luxon";
import Button from "../../components/Shared/Button";
import { Combobox } from "@headlessui/react";
import AlgoliaSearch from "algoliasearch";
import { AlgoliaIndexes } from "../../constants";
import { ChevronDownIcon, ChevronUpIcon, EyeIcon, SearchIcon, TrashIcon, XIcon } from "@heroicons/react/outline";
import TextInput from "../../components/Shared/TextInput";
import { Tooltip } from "@material-tailwind/react";
import { useOnlineRegistrationFormUser } from "../../hooks/useOnlineRegistrationFormUser";

// Define types for lifted state
type TSessionSelection = Record<string, Record<string, boolean>>;
type TInitialRegistrations = Record<string, Set<string>>; // { [driverId]: Set<sku> }

interface ModalProps {
  id: number;
  show: boolean;
  handleClose: () => void;
  order: IOrder;
  view: TBookingType;
  selectedDate: Date;
  handleViewOrderClick: (id: number) => void;
  onActivityChange?: (activity: TBookingType) => void;
}

type TIsChanged = "ADDED" | "REMOVED" | boolean;

export default function DriverRegistrationModal(props: ModalProps) {
  const { id, order, show, handleClose, view, selectedDate, handleViewOrderClick, onActivityChange } = props;
  const { bookings, bookingsLoading, driversLoading, drivers, addToSession, addDriverToRace, removeDriverFromRace } = useDriverRegistration();
  const qrCodeParent = useRef<HTMLDivElement | null>(null);
  const [qrCodeWidth, setQrCodeWidth] = useState(0);
  const [isAddedingToSession, setIsAddedingToSession] = useState(false);
  const [addingDriverToRace, setAddingDriverToRace] = useState(false);
  const { count } = useOnlineRegistrationFormUser(order.id);
  const [searchText, setSearchText] = useState("");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("asc");
  const [sessionSelection, setSessionSelection] = useState<TSessionSelection>({});
  const [initialRegistrations, setInitialRegistrations] = useState<TInitialRegistrations>({});

  const orderId = useMemo(() => String(order.id), [order]);
  const bookerEmail = useMemo(() => order.email, [order]);

  const booking = useMemo(() => {
    const b = bookings?.[order?.id];
    if (b && b.drivers && b.drivers.length) {
      //sort drivers
      b.drivers.sort((a, b) => {
        if (drivers?.[a]?.first_name?.toLowerCase() < drivers?.[b]?.first_name?.toLowerCase()) return -1;
        if (drivers?.[a]?.first_name?.toLowerCase() > drivers?.[b]?.first_name?.toLowerCase()) return 1;
        return 0;
      });
    }
    return b;
  }, [order, bookings, drivers]);

  const RegistrationContent = useMemo(() => `https://registration.hyperkarting.com.au/bookings/${order.id}`, [order]);

  const RegistrationQRContent = useMemo(() => `https://registration.hyperkarting.com.au/bookings/${order.id}?registration_source=in_venue`, [order]);

  // Filtered Line Items based on view, date, and refunds
  const lineItems = useMemo(() => order.line_items.filter((li) => {
      const track = getTrackFromSku(li.sku);
      if (li.sku && track !== view) {
        return false;
      }
      const isRefunded = (order?.refunds ?? []).find((refund) => (refund?.refund_line_items ?? []).find((rli) => rli.line_item_id === li.id && rli.quantity === li.quantity));
      if (isRefunded || ("current_quantity" in li && (li as any).current_quantity === 0)) {
        return false;
      }
      const lineItemDate = getDateFromSku(li.sku);
      return lineItemDate && lineItemDate.toDateString() === selectedDate.toDateString();
    })
    .sort((prev, next) => {
      const prevDt = getDatetimeFromSku(prev.sku);
      const nextDt = getDatetimeFromSku(next.sku);
      return prevDt!.toMillis() - nextDt!.toMillis();
    }),[order, view, selectedDate]);
    

  // Initialize/Reset sessionSelection and initialRegistrations based on booking data
  useEffect(() => {
    if (booking) {
      const initialRegs: TInitialRegistrations = {};
      const initialSelection: TSessionSelection = {};

      (booking.registrations || []).forEach(reg => {
        if (!initialRegs[reg.driverId]) initialRegs[reg.driverId] = new Set();
        initialRegs[reg.driverId].add(reg.sku);

        if (!initialSelection[reg.driverId]) initialSelection[reg.driverId] = {};
        initialSelection[reg.driverId][reg.sku] = true;
      });

      setInitialRegistrations(initialRegs);
      setSessionSelection(initialSelection);
    } else {
      // Reset state if booking becomes null/undefined
      setInitialRegistrations({});
      setSessionSelection({});
    }
    // Reset search and sort when booking changes
    setSearchText("");
    setSortDirection("asc");
  }, [booking]); // Rerun when booking changes

  // Filtered and Sorted Drivers based on search and sort state
  const filteredSortedDrivers = useMemo(
    () =>
      (booking?.drivers || [])
        .filter((id) => {
          const driver = drivers[id];
          if (!driver) return false;
          return `${driver.first_name} ${driver.last_name} ${driver.email} ${driver.phone_number}`.toLowerCase().includes(searchText.toLowerCase());
        })
        .sort((a, b) => {
          const driverA = drivers[a];
          const driverB = drivers[b];
          if (!driverA || !driverB) return 0;
          const sort = sortDirection === "asc" ? 1 : -1;
          return driverA.first_name.localeCompare(driverB.first_name) * sort;
        }),
    [booking, searchText, drivers, sortDirection]
  );

  // Callback to handle individual session checkbox changes
  const handleSessionSelectionChange = useCallback((driverId: string, sku: string, isChecked: boolean) => {
    setSessionSelection(prev => {
      const newState = deepCopy(prev); // Use deepCopy or structuredClone for safety
      if (!newState[driverId]) newState[driverId] = {};
      newState[driverId][sku] = isChecked;
      return newState;
    });
  }, []);

  /** 
   * Helper function to compare current session selection state with initial registrations
   * and determine which registrations need to be added or removed
   * @param selection Current session selection state mapping driver IDs to selected SKUs
   * @returns {Object} Changes to be made
   * @returns {IRegistration[]} .added - Array of registrations to be added
   * @returns {IRegistration[]} .removed - Array of registrations to be removed
  */
  const getSessionChanges = useCallback((selection:TSessionSelection)=>{
    const currentAdded: IRegistration[] = [];
    const currentRemoved: IRegistration[] = [];

    Object.entries(selection).forEach(([driverId, skus]) => {
      Object.entries(skus).forEach(([sku, isSelected]) => {
        const wasInitiallyRegistered = initialRegistrations[driverId]?.has(sku) ?? false;
        const registrationData = { driverId, orderId, sku, raceType: view };

        if (isSelected && !wasInitiallyRegistered) {
          currentAdded.push(registrationData);
        } else if (!isSelected && wasInitiallyRegistered) {
          currentRemoved.push(registrationData);
        }
      });
    });

     // Also consider drivers completely removed from selection who were initially registered
     Object.entries(initialRegistrations).forEach(([driverId, initialSkus]) => {
         if (!selection[driverId]) { // Driver row might not exist in selection if deselected entirely
             initialSkus.forEach(sku => {
                 currentRemoved.push({ driverId, orderId, sku, raceType: view });
             });
         }
     });

    return { added: currentAdded, removed: currentRemoved };
  }, [initialRegistrations, orderId, view])

  // Derive 'added' and 'removed' based on current selection vs initial state
  const { added, removed } = useMemo(() => getSessionChanges(sessionSelection), [sessionSelection, getSessionChanges]);

  // Check if anything changed compared to initial state
  const isAnythingChanged = useCallback(
    (driverId: string, sku: string): TIsChanged => {
      const isSelected = sessionSelection[driverId]?.[sku] ?? false;
      const wasInitiallyRegistered = initialRegistrations[driverId]?.has(sku) ?? false;

      if (isSelected && !wasInitiallyRegistered) return "ADDED";
      if (!isSelected && wasInitiallyRegistered) return "REMOVED";
      return false;
    },
    [sessionSelection, initialRegistrations]
  );

  const someThingChanged = useMemo(() => !!(added.length || removed.length), [added, removed]);

  // Handler for the master "Add to Session" / "Save Changes" button
  const handleConfirmChanges = useCallback(async () => {
    try {
      setIsAddedingToSession(true);
      const data = await addToSession({ added, removed, orderId });
      if (data.success) {
        // Force re-evaluation of initial state after successful save
        // This is done implicitly by the context update triggering the useEffect dependency on `booking`
        // Optionally clear added/removed derived state immediately for UI feedback,
        // but the source `sessionSelection` should reflect the new persisted state after context updates.
      }
    } catch (error) {
      console.log(error);
      alert(`Error saving changes: ${error}`); // Provide user feedback
    } finally {
      setIsAddedingToSession(false);
    }
  }, [addToSession, added, removed, orderId]);

  // Handler for the "Select all time" button
  const handleSetAllTime = useCallback((newDriverState: boolean) => {
    const newState = deepCopy(sessionSelection);

    (booking?.drivers || []).forEach(driverId => {
      if (!newState[driverId]) {
        newState[driverId] = {};
      }
      lineItems.forEach(li => {
        if(newState[driverId][li.sku] !== newDriverState){
          newState[driverId][li.sku] = newDriverState;
        }
      });
    });

    // Select all time slots for all drivers
    setSessionSelection(newState);

    return newState;
  }, [lineItems, booking?.drivers, sessionSelection]);

  const handleSelectAllTime = useCallback(() => handleSetAllTime(true), [handleSetAllTime]);
  const handleDeselectAllTime = useCallback(() => handleSetAllTime(false), [handleSetAllTime]);

  const handleAddAllToSession = useCallback(async () => {
    try {
      setIsAddedingToSession(true);

      const changes = handleSetAllTime(true);

      const { added } = getSessionChanges(changes);

      if (added.length < 0) {
        return;
      }
      
      const data = await addToSession({ 
        added, 
        removed: [], 
        orderId 
      });

      console.log("data", data)
    } catch (error) {
      console.error('Error in Add All To Session:', error);
      alert(`Error adding all to session: ${error}`);
    } finally {
      setIsAddedingToSession(false);
    }
  }, [orderId, getSessionChanges, handleSetAllTime, addToSession]);

  const handleAddDriverToBooking = useCallback(
    async (driver: TDriverIndexResult | null) => {
      if (!driver) return;
      setAddingDriverToRace(true);
      try {
        const data = await addDriverToRace(driver.objectID, orderId);
        console.log(data);
        // Potentially trigger a re-fetch or update local state if needed after adding
      } catch (error) {
        console.error(error);
        alert(`Error adding driver: ${error}`);
      } finally {
        setAddingDriverToRace(false);
      }
    },
    [orderId, addDriverToRace]
  );

  // Handler for removing a driver from the booking entirely
  const handleRemoveDriverFromBooking = useCallback(async (driverIdToRemove: string) => {
    if (!window.confirm(`Are you sure you want to remove this driver from the booking? This cannot be undone.`)) {
      return;
    }
    try {
      // Consider adding loading state specific to this row/action
      await removeDriverFromRace(orderId, driverIdToRemove);
      alert("Driver removed successfully.");
      // Context update should handle removing the driver from the list
    } catch (error) {
      console.error("Error removing driver:", error);
      alert(`Failed to remove driver: ${error}`);
    }
  }, [orderId, removeDriverFromRace]);

  //TODO for today's races
  // const lineItems = useMemo(...) // Moved higher up

  const noOfDriversRegistered = useMemo(() => {
    return Array.isArray(booking?.drivers) ? booking.drivers.length : 0;
  }, [booking]);

  const noOfRacesSelected = useMemo(() => {
      // Calculate based on 'added' state derived from sessionSelection vs initialRegistrations
      const selectedSkus = new Set<string>();
      added.forEach(reg => selectedSkus.add(reg.sku));
      return selectedSkus.size;
  }, [added]);

  useEffect(() => {
    let resizeObserver = new ResizeObserver(() => {
      const width = qrCodeParent.current?.getBoundingClientRect?.()?.width;

      if (qrCodeParent.current && width !== undefined) {
        setQrCodeWidth(width);
      }
    });

    if (qrCodeParent.current) {
      resizeObserver.observe(qrCodeParent.current);
    }

    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  // Get unique activities from line items, including current view
  const availableActivities = useMemo(() => {
    const activities = Array.from(new Set(order.line_items
      .map(item => item.sku ? getTrackFromSku(item.sku) : null)
      .filter((track): track is TBookingType => track !== null)));
    
    // Sort based on the order in BOOKING_TYPES
    return activities.sort((a, b) => 
      BOOKING_TYPES.indexOf(a) - BOOKING_TYPES.indexOf(b)
    );
  }, [order.line_items]);

   // Calculate warning based on current selection state
   const showMaxDriverByLineItemWarning = useMemo(() => {
     return lineItems.some(li => {
       const currentQuantity = isNaN((li as any).current_quantity) ? li.quantity : (li as any).current_quantity;
       let countForSku = 0;
       Object.entries(sessionSelection).forEach(([driverId, skus]) => {
         if (skus[li.sku]) { // Check if selected in the current state
           countForSku++;
         }
       });
       return countForSku > currentQuantity;
     });
   }, [lineItems, sessionSelection]); // Depends on sessionSelection now

  // Toggle sort direction
  const toggleSortDirection = useCallback(() => {
      setSortDirection((oldDirection) => (oldDirection === "asc" ? "desc" : "asc"));
  }, []);

  // Handle the master checkbox for selecting/deselecting all drivers
  const handleSelectAllDriversChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
      const isChecked = e.target.checked;
      if (!isChecked) {
           // Reset sessionSelection for ALL drivers to their initial state
           setSessionSelection(() => {
               const initialSelection: TSessionSelection = {};
               (booking?.drivers || []).forEach(driverId => {
                   if (!initialSelection[driverId]) initialSelection[driverId] = {};
                   lineItems.forEach(li => {
                       const originallyRegistered = initialRegistrations[driverId]?.has(li.sku) ?? false;
                       initialSelection[driverId][li.sku] = originallyRegistered;
                   });
               });
               return initialSelection;
           });
      }
  }, [booking?.drivers, lineItems, initialRegistrations]); // Added dependencies

  return (
    <Modal
      title={
        handleViewOrderClick ? (
          <div className="flex items-center justify-between w-full">
            <div className="flex items-center gap-2">
              <p className={`${view === "BATTLE_KART" ? "text-track-battle" : ""}`}>Registration Details</p>
              <Button
                variant="small"
                type="secondary"
                onClick={() => {
                  handleClose();
                  handleViewOrderClick(id);
                }}
                className={`${view === "BATTLE_KART" ? "border-track-battle text-track-battle" : ""}`}
              >
                Booking Details
              </Button>
            </div>
            <div className="flex items-center gap-2 mr-4">
              {availableActivities.map(activity => (
                <Button
                  key={activity}
                  variant="small"
                  type={activity === view ? "primary" : "secondary"}
                  onClick={() => activity !== view ? onActivityChange?.(activity) : undefined}
                  className={`${activity === "BATTLE_KART" ? `bg-track-battle hover:bg-track-battle/90 ${activity !== view ? "text-track-battle border-track-battle hover:text-white" : ""}` : ""}`}
                >
                  {getTrackDisplayName(activity)}
                </Button>
              ))}
            </div>
          </div>
        ) : (
          "Registration Details"
        )
      }
      show={show}
      onClose={handleClose}
      className="max-w-[95vw] h-[95vh]"
    >
      <div className="flex justify-between p-5 gap-6 h-[calc(100%-65px)] overflow-hidden">
        <div className="w-[25%] flex flex-col gap-2 overflow-y-auto">
          <ModalSection title="QR Code" titleClassName={`${view === "BATTLE_KART" ? "text-track-battle" : ""}`}>
            <div ref={qrCodeParent}>
              {RegistrationQRContent && (
                <>
                  <div className="flex justify-center">
                    <QrCode renderAs="svg" value={RegistrationQRContent} level="H" includeMargin size={qrCodeWidth * 0.7} />
                  </div>
                  <a href={RegistrationContent} target="_blank" className="break-words text-gray-800 not-italic font-medium text-xs leading-none" rel="noreferrer">
                    {RegistrationContent}
                  </a>
                </>
              )}
            </div>
          </ModalSection>
          <ModalSection title="Status" titleClassName={`${view === "BATTLE_KART" ? "text-track-battle" : ""}`} headerContent={count ? (
            <div className="flex items-center ml-2">
              <EyeIcon className="w-4" />
              <p className="text-xs leading-none ml-1">{getLiveUsersRegistrationContent(count)}</p>
            </div>
          ) : <></>}>
            <div className="text-gray-800 not-italic font-medium text-xs leading-none flex flex-col gap-2">
              <p>{noOfRacesSelected} Races Selected</p>
              <p>{noOfDriversRegistered} Drivers Registered</p>
            </div>
          </ModalSection>
        </div>
        <div className="w-[75%] overflow-auto">
          {bookingsLoading ? (
            <>
              <LoadingSpinner />
            </>
          ) : (
            <ModalSection
              title="Racers"
              titleClassName={`${view === "BATTLE_KART" ? "text-track-battle" : ""}`}
              headerContent={
                <div className="ml-auto flex justify-center items-center gap-2">
                  <Button variant="small" onClick={handleAddAllToSession} type="primary" className={`${view === "BATTLE_KART" ? "bg-track-battle" : "bg-[rgb(147,51,234)]"}`} loading={isAddedingToSession}>
                    {`Add All To ${view === "BATTLE_KART" ? "Game" : "Session"}`}
                  </Button>
                  <Button variant="small" onClick={handleSelectAllTime} type="primary" className={`${view === "BATTLE_KART" ? "bg-track-battle/90" : "bg-[#ff00ff]"}`}>
                    Select all
                  </Button>
                  <Button variant="small" onClick={handleDeselectAllTime} type="primary" className={`${view === "BATTLE_KART" ? "bg-track-battle/70" : "bg-[#9D62FE]"}`}>
                    Deselect all
                  </Button>
                </div>
              }
            >
              <div className="flex gap-4">
                <div className="flex-1">{addingDriverToRace ? <LoadingSpinner /> : <AddNewDriver handleAddDriverToBooking={handleAddDriverToBooking} />}</div>
              </div>
              <div className="flex flex-col gap-2 mt-2">
                {driversLoading ? ( addingDriverToRace ? (<></>) : <LoadingSpinner/>) : (
                  <RegistrationTable
                    lineItems={lineItems}
                    view={view}
                    drivers={drivers}
                    filteredSortedDrivers={filteredSortedDrivers}
                    orderId={orderId}
                    bookerEmail={bookerEmail}
                    booking={booking}
                    sessionSelection={sessionSelection}
                    handleSessionSelectionChange={handleSessionSelectionChange}
                    isAnythingChanged={isAnythingChanged}
                    searchText={searchText}
                    handleSearchTextChange={setSearchText}
                    sortDirection={sortDirection}
                    toggleSortDirection={toggleSortDirection}
                    handleSelectAllDriversChange={handleSelectAllDriversChange}
                    handleRemoveDriverFromBooking={handleRemoveDriverFromBooking}
                    showMaxDriverByLineItemWarning={showMaxDriverByLineItemWarning}
                  />
                )}
              </div>
            </ModalSection>
          )}
        </div>
      </div>

      <div className="flex justify-end gap-2">
        <div
          className={`cursor-pointer inline-flex justify-center rounded-full border border-transparent px-4 py-2 text-sm font-medium text-white ${view === "BATTLE_KART" ? "bg-track-battle hover:bg-track-battle/90" : "bg-purple-500 hover:bg-purple-600"}`}
          onClick={handleClose}
        >
          Back
        </div>
        <button
          className={`inline-flex justify-center rounded-full border border-transparent  px-4 py-2 text-sm font-medium text-white ${view === "BATTLE_KART" ? "bg-track-battle hover:bg-track-battle/90" : "bg-purple-500 hover:bg-purple-600"}  ${
            someThingChanged || isAddedingToSession ? "cursor-pointer" : "bg-opacity-30 pointer-events-none"
          }`}
          disabled={!someThingChanged || isAddedingToSession}
          onClick={handleConfirmChanges}
        >
          {isAddedingToSession ? "..." : (removed.length ? "Save Changes" : `Add to ${view === "BATTLE_KART" ? "Game" : "Session"}`)}
        </button>
      </div>
    </Modal>
  );
}

interface IRegistrationTableProps {
  lineItems: IOrderLineItem[];
  view: TBookingType;
  drivers: Record<string, IDriver | undefined>;
  filteredSortedDrivers: string[];
  booking: IFirebaseBookingDocument | undefined | null;
  orderId: string;
  bookerEmail: string;
  showMaxDriverByLineItemWarning: boolean;
  sessionSelection: TSessionSelection;
  handleSessionSelectionChange: (driverId: string, sku: string, isChecked: boolean) => void;
  isAnythingChanged: (firebaseId: string, sku: string) => TIsChanged;
  searchText: string;
  handleSearchTextChange: (text: string) => void;
  sortDirection: "asc" | "desc";
  toggleSortDirection: () => void;
  handleSelectAllDriversChange: React.ChangeEventHandler<HTMLInputElement>;
  handleRemoveDriverFromBooking: (driverIdToRemove: string) => Promise<void>;
}

const RegistrationTable: React.FC<IRegistrationTableProps> = ({
  lineItems,
  view,
  drivers,
  filteredSortedDrivers,
  booking,
  orderId,
  bookerEmail,
  showMaxDriverByLineItemWarning,
  sessionSelection,
  handleSessionSelectionChange,
  isAnythingChanged,
  searchText,
  handleSearchTextChange,
  sortDirection,
  toggleSortDirection,
  handleSelectAllDriversChange,
  handleRemoveDriverFromBooking,
}) => {
  const maxDriversInOneSession = useMemo(() => {
    return lineItems.reduce((max, li) => {
      return Math.max(max, (isNaN((li as any).current_quantity) ? li.quantity : (li as any).current_quantity));
    }, 0);
  }, [lineItems]);

  const totalSessions: number = useMemo(() => {
    let totalQuantity = 0;
    lineItems.forEach((li) => {
      totalQuantity += (isNaN((li as any).current_quantity) ? li.quantity : (li as any).current_quantity);
    });
    return totalQuantity;
  }, [lineItems]);

  const bookerDriverProfile = useMemo(() => {
    const bookerId = booking?.drivers?.find(id => ((drivers[id]?.email === bookerEmail) && (!drivers[id]?.isJunior)));
    return bookerId ? drivers[bookerId] : null;
  }, [booking, drivers, bookerEmail]);

  const driverInfoContent = useMemo(() => {
    if(filteredSortedDrivers.length < maxDriversInOneSession){
      return {
        content: `Minimum of ${maxDriversInOneSession} driver${maxDriversInOneSession > 1 ? "s" : ""} expected for this booking (${filteredSortedDrivers.length} listed).`,
        color: "text-[#FFA500]"
      }
    }else if (maxDriversInOneSession <= filteredSortedDrivers.length && filteredSortedDrivers.length <= totalSessions){
      return {
        content: `Maximum of ${totalSessions} driver${totalSessions > 1 ? "s" : ""} expected for this booking (${filteredSortedDrivers.length} listed).`,
        color: "text-[#28a745]"
      }
    }else{
      return {
        // content: `Maximum of ${totalSessions} Drivers expected for this booking (${filteredSortedDrivers.length} listed).`,
        content: `Maximum of ${totalSessions} driver${totalSessions > 1 ? "s" : ""} expected for this activity.`,
        color: "text-[red]"
      }
    }
  },[filteredSortedDrivers, maxDriversInOneSession, totalSessions])

  if (!booking?.drivers?.length) {
    return <div>No registration found</div>;
  }

  return (
    <table className="min-w-full h-[1px] border-separate border-spacing-y-2">
      <thead>
        <tr>
          <th className="text-gray-800 not-italic font-medium font-[montserrat] text-xs leading-10 flex gap-2 pl-2 items-center">
            <div onClick={toggleSortDirection} className="cursor-pointer">
              Name
            </div>
            <div className="cursor-pointer" onClick={toggleSortDirection}>
              {sortDirection === "asc" ? <ChevronUpIcon className="w-4" /> : <ChevronDownIcon className="w-4" />}
            </div>
            <TextInput
              className="font-normal text-sm pl-2 px-2.5 py-1 flex-grow-0"
              placeholder="Type here..."
              value={searchText}
              onChange={handleSearchTextChange}
            />
          </th>
          {lineItems.map((li) => <LineItemHeader li={li} view={view} key={li.id} />)}
        </tr>
      </thead>
      <tbody>
        {filteredSortedDrivers.map((firebaseId, index) => (
          <DriverRow
            key={firebaseId}
            firebaseId={firebaseId}
            driverData={drivers[firebaseId]}
            index={index + 1}
            lineItems={lineItems}
            orderId={orderId}
            view={view}
            registrations={booking?.registrations || []}
            sessionSelection={sessionSelection}
            handleSessionSelectionChange={handleSessionSelectionChange}
            isAnythingChanged={isAnythingChanged}
            handleRemoveDriverFromBooking={handleRemoveDriverFromBooking}
            isAutoAddedToSession={!!booking?.registrations?.find((r) => r.driverId === firebaseId && r.autoAddedToSession)}
            isBooker={bookerDriverProfile?.firebase_id === firebaseId}
          />
        ))}
        <tr>
          <td colSpan={lineItems.length + 1}> 
            <div className="flex justify-between pt-2">
              <p className={`${driverInfoContent.color} not-italic font-medium font-[montserrat] text-sm`}>{driverInfoContent.content}</p>
              {showMaxDriverByLineItemWarning && <p className="text-[red] not-italic font-medium font-[montserrat] text-sm">Warning: More drivers selected for a session than available seats.</p>}
            </div>
          </td>
        </tr>
      </tbody>
    </table>
  );
};

interface IDriverRowProps {
  firebaseId: string;
  driverData: IDriver | undefined;
  index: number;
  lineItems: IOrderLineItem[];
  orderId: string;
  view: TBookingType;
  registrations: IRegistration[];
  sessionSelection: TSessionSelection;
  handleSessionSelectionChange: (driverId: string, sku: string, isChecked: boolean) => void;
  isAnythingChanged: (firebaseId: string, sku: string) => TIsChanged;
  handleRemoveDriverFromBooking: (driverIdToRemove: string) => Promise<void>;
  isAutoAddedToSession: boolean;
  isBooker: boolean;
}

const DriverRow: React.FC<IDriverRowProps> = ({
  firebaseId,
  driverData,
  index,
  lineItems,
  orderId,
  registrations,
  sessionSelection,
  handleSessionSelectionChange,
  isAnythingChanged,
  view,
  handleRemoveDriverFromBooking,
  isAutoAddedToSession,
  isBooker
}) => {
  const [isDeletingDriver, setIsDeletingDriver] = useState(false);

  const isBirthday = useMemo(() => {
    if (!driverData?.dob || !lineItems[0]?.sku) return false;
    const dob = DateTime.fromFormat(driverData.dob, "yyyy-MM-dd");
    const sessionDate = getDatetimeFromSku(lineItems[0].sku);
    return dob.isValid && sessionDate?.isValid && dob.toFormat("MM-dd") === sessionDate.toFormat("MM-dd");
  }, [driverData, lineItems]);

  const age = useMemo(() => {
    const dt = DateTime.fromFormat(driverData?.dob ?? "", "yyyy-MM-dd");
    return dt.isValid ? `${getAgeInYears(dt)} Year Old` : null;
  }, [driverData]);

  const registration = useMemo(()=> registrations.find((registration) => registration.driverId === firebaseId), [registrations, firebaseId])
  const isDriverAddedToAnySession = useMemo(() => !!registration,[registration]);
  const visitingFirstTime = useMemo(()=> driverData && !+(driverData.total_visits || 0), [driverData])

  // Check if all line items for this driver are selected
  const areAllLineItemsSelected = useMemo(() => {
    return lineItems.every(li => sessionSelection[firebaseId]?.[li.sku] === true);
  }, [lineItems, sessionSelection, firebaseId]);

  const handleRowCheckboxChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const isChecked = e.target.checked;
    // Update all line items for this driver
    lineItems.forEach(li => {
      handleSessionSelectionChange(firebaseId, li.sku, isChecked);
    });
  }, [firebaseId, lineItems, handleSessionSelectionChange]);

  const handleDeleteDriverClick = useCallback(async () => {
    setIsDeletingDriver(true);
    await handleRemoveDriverFromBooking(firebaseId);
  }, [firebaseId, handleRemoveDriverFromBooking]);

  if (!driverData) {
    return <tr className="bg-[#F3F4F7] h-10"><td colSpan={lineItems.length + 1}></td></tr>;
  }

  return (
    <tr className="bg-[#F3F4F7]" data-racefacer-uuid={driverData.racefacer_uuid} data-driver-id={firebaseId}>
      <td className="w-[100%] bg-white p-0">
        <div className="text-gray-700 not-italic font-medium font-[montserrat] text-xs leading-10 bg-[#F3F4F7] rounded-md px-2 mr-2 flex gap-2 capitalize items-center">
          <input
            type="checkbox"
            className={`${view === "BATTLE_KART" ? "accent-track-battle" : "accent-[#7886FF]"} w-4`}
            checked={areAllLineItemsSelected}
            onChange={handleRowCheckboxChange}
          />
          <a href={`http://192.168.43.100/en/administration/clients/clients?uuid=${driverData.racefacer_uuid}`} rel="noreferrer" target="_blank" className="underline">
            <b>#{index}</b> {driverData.first_name} {driverData.last_name}
          </a>
          {isBirthday && (
            <Tooltip content="Birthday!">
              <span role="img" aria-label="birthday" className="text-lg">
                🎈
              </span>
            </Tooltip>
          )}
          {isAutoAddedToSession && <Button variant="extraSmall" className="bg-green-600 cursor-default text-xs" > Auto added </Button>}
          {isBooker && <Button variant="extraSmall" className="bg-green-900 cursor-default text-xs" > Booker </Button>}
          {(visitingFirstTime && (view === "MAIN_TRACK" || view === "INTERMEDIATE_TRACK")) && <p className="text-red-500 text-xs" > First Time </p>}

          {age && <p className="text-xs text-gray-400 flex-1 text-right">{age}</p>}
          {isDeletingDriver ? (
            <div className="ml-auto px-2">Deleting...</div>
          ) : !isDriverAddedToAnySession ? (
            <Tooltip content="Remove driver from this booking">
              <TrashIcon
                className="w-4 ml-auto text-red-400 hover:text-red-600 cursor-pointer"
                onClick={handleDeleteDriverClick}
              />
            </Tooltip>
          ) : null
          }
        </div>
      </td>
      {lineItems.map((li, colIndex) => {
        const isRegistered = registrations.some(reg => reg.driverId === firebaseId && reg.sku === li.sku);
        return (
          <td key={li.id} className="text-center bg-white p-0">
            <div className={`h-full flex justify-center bg-[#F3F4F7] ${colIndex === 0 ? "rounded-l-md" : ""} ${colIndex === lineItems.length - 1 ? "rounded-r-md" : ""}`}>
              <AddedToSessionCheckbox
                sku={li.sku}
                checked={sessionSelection[firebaseId]?.[li.sku] ?? false}
                onChange={(e) => handleSessionSelectionChange(firebaseId, li.sku, e.target.checked)}
                changeStatus={isAnythingChanged(firebaseId, li.sku)}
                disabled={isRegistered}
                view={view}
              />
            </div>
          </td>
        );
      })}
    </tr>
  );
};

interface IAddedToSessionCheckboxProps {
  checked: boolean;
  sku: string;
  onChange: React.ChangeEventHandler<HTMLInputElement>;
  disabled?: boolean;
  changeStatus?: TIsChanged;
  view: TBookingType;
}

const AddedToSessionCheckbox: React.FC<IAddedToSessionCheckboxProps> = ({
  sku,
  onChange,
  checked,
  disabled,
  changeStatus,
  view
}: IAddedToSessionCheckboxProps) => {
  const bgColor = changeStatus === 'ADDED' ? 'bg-green-200' : changeStatus === 'REMOVED' ? 'bg-red-200' : '';

  const inputStyle = disabled ? { opacity: 0.6, filter: 'grayscale(100%)', cursor: 'pointer' } : { cursor: 'pointer' };

  return (
    <input style={inputStyle}
      type="checkbox"
      className={`${view === "BATTLE_KART" ? "accent-track-battle" : "accent-[#7886FF]"} w-4 ${bgColor}`}
      value={sku}
      onChange={onChange}
      checked={checked}
      data-value={sku}
    />
  );
};

const AddNewDriver = ({ handleAddDriverToBooking }: { handleAddDriverToBooking: (driver: TDriverIndexResult | null) => void }) => {
  const [query, setQuery] = useState("");
  const [drivers, setDrivers] = useState<TDriverIndexResult[]>([]);
  const [loading, setLoading] = useState(false);
  const debounceSearchTimeoutRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const client = AlgoliaSearch(process.env.REACT_APP_ALGOLIA_APP_ID as string, process.env.REACT_APP_ALGOLIA_SEARCH_API_KEY as string);
  const index = client.initIndex(AlgoliaIndexes.Drivers);

  const searchDriver: ChangeEventHandler<HTMLInputElement> = useCallback((event) => {
    setLoading(true);
    const q = event.target.value;
    setQuery(q);
    if (debounceSearchTimeoutRef.current) {
      clearTimeout(debounceSearchTimeoutRef.current);
      debounceSearchTimeoutRef.current = null;
    }
    debounceSearchTimeoutRef.current = setTimeout(() => {
      index
        .search(q)
        .then(({ hits }) => {
          setDrivers(hits as TDriverIndexResult[]);
        })
        .catch(console.error)
        .finally(() => {
          setLoading(false);
        });
    }, 300);
    },[index]);

  const handleChange = useCallback(
    (driver: TDriverIndexResult) => {
      handleAddDriverToBooking(driver);
    },
    [handleAddDriverToBooking]
  );

  const handleClearSearch = useCallback(() => {
    setQuery("");
    setDrivers([]);
  }, []);

  return (
    <Combobox onChange={handleChange}>
      <div className="relative w-full flex items-center">
        <Combobox.Input
          onChange={searchDriver}
          className="block w-full pl-2 text-gray-900 border border-solid border-gray-300 rounded-full bg-gray-100 font-normal text-sm px-2.5 py-1"
          placeholder="Search..."
          displayValue={(driver: TDriverIndexResult) => (driver ? `${driver?.first_name} ${driver?.last_name} (${driver.email}, ${driver.phone_number})` : "")}
        />
        {query ? (
          <div className="absolute right-2.5 cursor-pointer text-[#200E32]" onClick={handleClearSearch}>
            <XIcon className="w-6" />
          </div>
        ) : (
          <div className="absolute right-2.5 text-[#200E32] pointer-events-none">
            <SearchIcon className="w-6" />
          </div>
        )}
      </div>
      <Combobox.Options className="bg-gray-100 rounded-lg p-2 max-h-[30vh] overflow-auto">
        {loading && <LoadingSpinner />}
        {drivers.map((driver) => (
          <div className="flex items-center gap-1">
            <a href={`http://192.168.43.100/en/administration/clients/clients?uuid=${driver.racefacer_uuid}`} rel="noreferrer" target="_blank" className="underline">
              <EyeIcon className="w-5 text-gray-600" />
            </a>

            <Combobox.Option key={driver.objectID} value={driver} className="text-sm py-1 cursor-pointer hover:bg-gray-300">
              {({ active }) => (
                <div className={`flex gap-2 justify-between ${active ? "bg-gray-300" : ""}`}>
                  <span>
                    {driver.first_name} {driver.last_name}
                  </span>
                  <span className="text-xs text-gray-400">
                    {driver.email} ({driver.phone_number})
                  </span>
                </div>
              )}
            </Combobox.Option>
          </div>
        ))}
      </Combobox.Options>
    </Combobox>
  );
};

const LineItemHeader = ({li, view}: {li: IOrderLineItem, view: TBookingType})=>{
  
  const ageGroupContent = useMemo(()=>{
    if(!["MAIN_TRACK", "MINI_TRACK"].includes(view)) return undefined;

    const ageGroupContent = getAgeGroupDisplayName(li.variant_title)

    if(ageGroupContent && ["<12", "12-15"].includes(ageGroupContent)){
      return ageGroupContent;
    }

    return undefined;
  }, [li, view])

  return (
    <th key={li.id} className="text-gray-800 not-italic font-medium font-[montserrat] text-xs text-center leading-[1.4]">
      <div className="mx-2">{getDatetimeFromSku(li.sku)?.toFormat("hh:mma")}</div>
      <div className="mx-2 flex justify-center">
        <div className="text-[red] w-max">{isNaN((li as any).current_quantity) ? li.quantity : (li as any).current_quantity}{ageGroupContent && ` (${ageGroupContent})`}</div>
      </div>
    </th>
  )
}